// Copyright © 2025 CATTLEytics Inc.

export function exportToCSV(data: any[], filename: string): void {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Get headers from the first row
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    // Header row
    headers.map((header) => escapeCSVValue(header)).join(','),
    // Data rows
    ...data.map((row) => headers.map((header) => escapeCSVValue(row[header])).join(',')),
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function escapeCSVValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  // Check if value needs escaping
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    // Escape double quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

export function exportPivotTableToCSV(
  data: any[],
  rowField: string,
  columnField: string,
  valueField: string,
  filename: string,
  showRowTotals = false,
  showColumnTotals = false,
): void {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Extract unique row and column values
  const rows = [...new Set(data.map((item) => item[rowField]))].sort();
  const columns = [...new Set(data.map((item) => item[columnField]))].sort();

  // Create pivot data structure
  const pivotData: { [row: string]: { [col: string]: any } } = {};

  // Initialize empty pivot table
  rows.forEach((row) => {
    pivotData[row] = {};
    columns.forEach((col) => {
      pivotData[row][col] = '';
    });
  });

  // Populate pivot data
  data.forEach((item) => {
    const row = item[rowField];
    const col = item[columnField];
    const val = item[valueField];

    if (pivotData[row] && pivotData[row][col] !== undefined) {
      pivotData[row][col] = val || '';
    }
  });

  // Build CSV rows
  const csvRows: string[][] = [];

  // Header row
  const headerRow = [rowField, ...columns];
  if (showRowTotals) {
    headerRow.push('Total (Sum)');
  }
  csvRows.push(headerRow);

  // Data rows
  rows.forEach((row) => {
    const dataRow = [row];
    let rowTotal = 0;

    columns.forEach((col) => {
      const value = pivotData[row][col];
      dataRow.push(value);
      if (showRowTotals && value && !isNaN(Number(value))) {
        rowTotal += Number(value);
      }
    });

    if (showRowTotals) {
      dataRow.push(rowTotal > 0 ? String(rowTotal) : '');
    }

    csvRows.push(dataRow);
  });

  // Column totals row
  if (showColumnTotals) {
    const totalRow = ['Total (Sum)'];
    let grandTotal = 0;

    columns.forEach((col) => {
      let colTotal = 0;
      rows.forEach((row) => {
        const value = pivotData[row][col];
        if (value && !isNaN(Number(value))) {
          colTotal += Number(value);
        }
      });
      totalRow.push(colTotal > 0 ? String(colTotal) : '');
      grandTotal += colTotal;
    });

    if (showRowTotals) {
      totalRow.push(String(grandTotal));
    }

    csvRows.push(totalRow);
  }

  // Convert to CSV string
  const csvContent = csvRows
    .map((row) => row.map((cell) => escapeCSVValue(cell)).join(','))
    .join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
